<script lang="ts">
  /**
   * MetricDetailPage — navegação em duas camadas para drill-down de métricas.
   *
   * Camada 1 (ranking): lista de colaboradores ordenada pelo total desc.
   *   Header: "Total por colaborador — N afetados, X dias considerados no total"
   *   Click em linha → navega para camada 2.
   *
   * Camada 2 (breakdown diário): marcações do colaborador selecionado, dia a dia.
   *   Colunas: Data, Dia, Entrada, Pausa, Retorno, Saída, Esperado, Trabalhado, Déficit, Status.
   *   Botão "← Voltar ao ranking" volta para camada 1.
   *
   * Privacidade: usa apenas AnonymousId / rótulos de índice. Sem PII.
   */
  import type { DiagnosticResult } from '$core/diagnostic.types';
  import type { DiagnosticAssumptions } from '$core/domain/canonical.types';
  import {
    getDrillDownMeta,
    getDrillDownRanking,
    getEmployeeDailyBreakdown,
    getInferredEmployeeJourneyMinutes,
  } from '$core/metrics/drillDown';

  export let result: DiagnosticResult;
  export let metricId: string;
  export let assumptions: DiagnosticAssumptions;
  export let onBack: () => void;
  /** ID do colaborador atualmente focado; undefined = mostra ranking. */
  export let focusedEmployeeId: string | undefined = undefined;
  /** Callback chamado ao clicar numa linha do ranking. */
  export let onFocusEmployee: (employeeId: string) => void = () => {};
  /** Callback chamado ao clicar em "Voltar ao ranking". */
  export let onUnfocusEmployee: () => void = () => {};

  $: meta = getDrillDownMeta(metricId);
  $: ranking = getDrillDownRanking(result, metricId, assumptions);

  // Header stats para o ranking
  $: totalEmployeesAffected = ranking.length;
  $: totalDaysConsidered = ranking.reduce((acc, r) => acc + r.daysConsidered, 0);

  // Dados do colaborador focado
  $: focusedRankingEntry = focusedEmployeeId
    ? ranking.find(r => r.employeeId === focusedEmployeeId)
    : undefined;
  $: dailyBreakdown = focusedEmployeeId
    ? getEmployeeDailyBreakdown(result, metricId, focusedEmployeeId, assumptions)
    : [];
  $: inferredJourneyMin = focusedEmployeeId
    ? getInferredEmployeeJourneyMinutes(result, focusedEmployeeId)
    : null;

  /**
   * Formata um total de minutos como "Xh Ymin" (ex.: 5210 → "86h 50min").
   * Para valores < 60min, mostra só "Ymin". 0 minutos retorna "0min".
   */
  function formatHoursMinutes(totalMin: number): string {
    const sign = totalMin < 0 ? '-' : '';
    const abs = Math.abs(Math.round(totalMin));
    if (abs === 0) return '0min';
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    if (h === 0) return `${sign}${m}min`;
    if (m === 0) return `${sign}${h}h`;
    return `${sign}${h}h ${m}min`;
  }

  /** True quando o totalValue do ranking representa minutos (vs. ocorrências). */
  function isMinutesMetric(label: string | undefined): boolean {
    return typeof label === 'string' && label.toLowerCase().includes('minuto');
  }

  /**
   * Formata o saldo do dia em "Xh Ymin": positivo = hora extra (`+Xh Ymin`),
   * negativo = déficit (`-Xh Ymin`). Null = marcações ímpares (não calculável).
   */
  function formatSaldo(saldoMin: number | null): string {
    if (saldoMin === null) return '—'; // marcações ímpares — não calculável
    if (saldoMin === 0) return '—';
    const formatted = formatHoursMinutes(saldoMin);
    if (saldoMin > 0) return `+${formatted}`;
    return formatted; // formatHoursMinutes preserva o sinal negativo
  }
</script>

<div class="metric-detail-page">
  <div class="metric-detail-page__header">
    <button
      class="metric-detail-page__back"
      type="button"
      on:click={focusedEmployeeId !== undefined ? onUnfocusEmployee : onBack}
      aria-label={focusedEmployeeId !== undefined ? 'Voltar ao ranking' : 'Voltar ao dashboard'}
    >
      {focusedEmployeeId !== undefined ? '← Voltar ao ranking' : '← Voltar ao dashboard'}
    </button>

    <h2 class="metric-detail-page__title">
      {meta?.title ?? metricId}
    </h2>

    {#if focusedEmployeeId === undefined}
      <p class="metric-detail-page__subtitle">
        Total por colaborador — {totalEmployeesAffected}
        {totalEmployeesAffected === 1 ? 'colaborador afetado' : 'colaboradores afetados'},
        {totalDaysConsidered}
        {totalDaysConsidered === 1 ? 'dia considerado' : 'dias considerados'} no total
      </p>
    {:else if focusedRankingEntry}
      {@const punchedCount = dailyBreakdown.filter(d => d.kind === 'punched').length}
      {@const inferredCount = dailyBreakdown.filter(d => d.kind === 'inferred-falta').length}
      {@const folgaCount = dailyBreakdown.filter(d => d.kind === 'folga').length}
      {@const showCalendar = inferredCount + folgaCount > 0}
      <p class="metric-detail-page__subtitle">
        {focusedRankingEntry.displayName} —
        {#if showCalendar}
          calendário do período: {punchedCount} com marcação,
          {inferredCount} {inferredCount === 1 ? 'falta inferida' : 'faltas inferidas'},
          {folgaCount} {folgaCount === 1 ? 'folga' : 'folgas'}
        {:else}
          {punchedCount} {punchedCount === 1 ? 'dia' : 'dias'} com marcação
        {/if}
        {#if inferredJourneyMin !== null}
          <span
            class="metric-detail-page__inferred"
            title="Estimada pela moda (≥50%) dos minutos trabalhados em dias com par completo. Quando o padrão é consistente, este número substitui a jornada-padrão das premissas no cálculo de saldo."
          >
            · Jornada inferida: <strong>{formatHoursMinutes(inferredJourneyMin)}/dia</strong>
          </span>
        {/if}
      </p>
    {/if}
  </div>

  <div class="metric-detail-page__body">
    {#if focusedEmployeeId === undefined}
      <!-- ===== RANKING VIEW ===== -->
      {#if ranking.length === 0}
        <p class="metric-detail-page__empty">Nenhum colaborador com ocorrências nesta métrica.</p>
      {:else}
        <div class="table-wrapper">
          <table class="data-table">
            <thead>
              <tr>
                <th class="align-center">#</th>
                <th class="align-left">Colaborador</th>
                <th class="align-right">Ocorrências</th>
                <th class="align-right">Dias c/ marcação</th>
                <th class="align-right">{isMinutesMetric(ranking[0]?.totalLabel) ? 'Total (h)' : (ranking[0]?.totalLabel ?? 'Total')}</th>
                {#if isMinutesMetric(ranking[0]?.totalLabel)}
                  <th class="align-right" title="Total dividido pelos dias com marcação">Média/dia útil</th>
                {/if}
              </tr>
            </thead>
            <tbody>
              {#each ranking as row, idx (row.employeeId)}
                {@const minutesMetric = isMinutesMetric(row.totalLabel)}
                {@const avgPerDay = row.daysConsidered > 0 ? row.totalValue / row.daysConsidered : 0}
                <tr
                  class="ranking-row"
                  role="button"
                  tabindex="0"
                  aria-label="Ver detalhes de {row.displayName}"
                  on:click={() => onFocusEmployee(row.employeeId)}
                  on:keydown={e => e.key === 'Enter' || e.key === ' ' ? onFocusEmployee(row.employeeId) : null}
                >
                  <td class="align-center rank-number">{idx + 1}</td>
                  <td class="align-left employee-name">{row.displayName}</td>
                  <td class="align-right">{row.eventCount}</td>
                  <td class="align-right">{row.daysConsidered}</td>
                  <td class="align-right total-value">
                    {minutesMetric ? formatHoursMinutes(row.totalValue) : row.totalValue}
                  </td>
                  {#if minutesMetric}
                    <td class="align-right">{formatHoursMinutes(avgPerDay)}</td>
                  {/if}
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    {:else}
      <!-- ===== BREAKDOWN DIÁRIO VIEW ===== -->
      {#if dailyBreakdown.length === 0}
        <p class="metric-detail-page__empty">Nenhum dado diário encontrado para este colaborador.</p>
      {:else}
        <div class="table-wrapper">
          <table class="data-table breakdown-table">
            <thead>
              <tr>
                <th class="align-left">Data</th>
                <th class="align-left">Dia</th>
                <th class="align-center">Entrada</th>
                <th class="align-center">Pausa</th>
                <th class="align-center">Retorno</th>
                <th class="align-center">Saída</th>
                <th class="align-right">Esperado</th>
                <th class="align-right">Trabalhado</th>
                <th class="align-right" title="Saldo do dia: positivo = hora extra, negativo = déficit">Saldo</th>
                <th class="align-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {#each dailyBreakdown as day (day.sessionKey)}
                {@const entrada = day.punches.find(p => p.kind === 'entrada')}
                {@const pausa = day.punches.find(p => p.kind === 'pausa')}
                {@const retorno = day.punches.find(p => p.kind === 'retorno')}
                {@const saida = day.punches.find(p => p.kind === 'saida')}
                <tr
                  class:breakdown-row--issue={day.hasIssue && day.kind === 'punched'}
                  class:breakdown-row--inferred-falta={day.kind === 'inferred-falta'}
                  class:breakdown-row--aej-absence={day.kind === 'aej-absence'}
                  class:breakdown-row--folga={day.kind === 'folga'}
                >
                  <td class="align-left">{day.date}</td>
                  <td class="align-left weekday">{day.weekday}</td>
                  {#if day.kind === 'punched'}
                    <td class="align-center punch-time">{entrada?.time ?? '—'}</td>
                    <td class="align-center punch-time">{pausa?.time ?? '—'}</td>
                    <td class="align-center punch-time">{retorno?.time ?? '—'}</td>
                    <td class="align-center punch-time">{saida?.time ?? '—'}</td>
                    <td class="align-right">{formatHoursMinutes(day.expectedMin)}</td>
                    <td class="align-right">{formatHoursMinutes(day.workedMin)}</td>
                    <td class="align-right saldo"
                        class:saldo--overtime={day.saldoMin !== null && day.saldoMin > 0}
                        class:saldo--deficit={day.saldoMin !== null && day.saldoMin < 0}
                        class:saldo--unknown={day.saldoMin === null}>
                      {formatSaldo(day.saldoMin)}
                    </td>
                    <td class="align-center">
                      {#if day.hasIncompletePunches}
                        <span class="status-badge status-badge--incomplete" title="Marcações ímpares — não dá pra inferir o que faltou (entrada, saída ou pausa). Precisa de correção manual.">⚠ Incompleto</span>
                      {:else if day.incompleteData}
                        <span class="status-badge status-badge--partial-data" title="Padrão sugere dados incompletos: tempo registrado muito abaixo do esperado ou intervalo longo entre batidas. Déficit não contabilizado como atraso — provável que faltem registros de retorno/saída.">~ Dados parciais</span>
                      {:else if day.hasIssue}
                        <span class="status-badge status-badge--issue" title={day.issues.join(' | ')}>⚠</span>
                      {:else}
                        <span class="status-badge status-badge--ok">✓</span>
                      {/if}
                    </td>
                  {:else}
                    <td class="align-center punch-time" colspan="4">
                      <span class="empty-row-summary">
                        {#if day.kind === 'aej-absence'}Falta registrada no AEJ
                        {:else if day.kind === 'inferred-falta'}Sem marcação em dia útil
                        {:else}—{/if}
                      </span>
                    </td>
                    <td class="align-right">{day.expectedMin > 0 ? formatHoursMinutes(day.expectedMin) : '—'}</td>
                    <td class="align-right">—</td>
                    <td class="align-right saldo"
                        class:saldo--deficit={day.saldoMin !== null && day.saldoMin < 0}>
                      {day.saldoMin !== null && day.saldoMin < 0 ? formatSaldo(day.saldoMin) : '—'}
                    </td>
                    <td class="align-center">
                      {#if day.kind === 'aej-absence'}
                        <span class="status-badge status-badge--issue" title={day.issues.join(' | ')}>Falta AEJ</span>
                      {:else if day.kind === 'inferred-falta'}
                        <span class="status-badge status-badge--issue" title={day.issues.join(' | ')}>Falta (inferida)</span>
                      {:else}
                        <span class="status-badge status-badge--folga" title="Dia sem marcação fora da heurística (provável folga, fim de semana ou feriado).">Folga</span>
                      {/if}
                    </td>
                  {/if}
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .metric-detail-page {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-5);
    max-width: 1200px;
    margin: 0 auto;
    padding: var(--ptt-space-6) var(--ptt-space-5);
  }

  .metric-detail-page__header {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-2);
  }

  .metric-detail-page__back {
    display: inline-flex;
    align-items: center;
    gap: var(--ptt-space-1);
    background: none;
    border: none;
    cursor: pointer;
    font-family: var(--ptt-font-sans, sans-serif);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--ptt-purple-100, #5B2FD4);
    padding: 0;
    width: fit-content;
    transition: opacity 0.15s ease;
  }

  .metric-detail-page__back:hover {
    opacity: 0.75;
  }

  .metric-detail-page__back:focus-visible {
    outline: 2px solid var(--ptt-purple-100, #5B2FD4);
    outline-offset: 2px;
    border-radius: var(--ptt-radius-sm, 4px);
  }

  .metric-detail-page__title {
    font-size: 1.375rem;
    font-weight: 700;
    color: var(--ptt-gray-300, #323232);
    margin: 0;
    padding-bottom: var(--ptt-space-2);
    border-bottom: 2px solid var(--ptt-gray-20, #e7e7e7);
  }

  .metric-detail-page__subtitle {
    font-size: 0.875rem;
    color: var(--ptt-gray-100, #8c8c8c);
    margin: 0;
  }

  .metric-detail-page__inferred {
    color: var(--ptt-purple-100, #5B2FD4);
    cursor: help;
  }

  .metric-detail-page__inferred strong {
    font-weight: 700;
  }

  .metric-detail-page__body {
    width: 100%;
  }

  .metric-detail-page__empty {
    font-size: 0.875rem;
    color: var(--ptt-gray-100, #8c8c8c);
    font-style: italic;
    margin: 0;
  }

  /* === Table base === */
  .table-wrapper {
    width: 100%;
    overflow-x: auto;
    border: 1px solid var(--ptt-gray-20, #e7e7e7);
    border-radius: var(--ptt-radius-md, 6px);
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--ptt-font-sans, sans-serif);
    font-size: 0.875rem;
  }

  .data-table thead tr {
    background: var(--ptt-gray-10, #f5f5f5);
  }

  .data-table th {
    padding: var(--ptt-space-3, 12px) var(--ptt-space-4, 16px);
    font-weight: 600;
    color: var(--ptt-gray-300, #323232);
    border-bottom: 1px solid var(--ptt-gray-20, #e7e7e7);
    white-space: nowrap;
  }

  .data-table td {
    padding: var(--ptt-space-3, 12px) var(--ptt-space-4, 16px);
    color: var(--ptt-gray-300, #323232);
    border-bottom: 1px solid var(--ptt-gray-20, #e7e7e7);
  }

  .data-table tbody tr:last-child td {
    border-bottom: none;
  }

  .data-table tbody tr:hover {
    background: var(--ptt-gray-10, #f5f5f5);
  }

  .align-left   { text-align: left; }
  .align-center { text-align: center; }
  .align-right  { text-align: right; }

  /* === Ranking === */
  .ranking-row {
    cursor: pointer;
    transition: background 0.1s ease;
  }

  .ranking-row:hover {
    background: var(--ptt-purple-10, #f0ebfd) !important;
  }

  .ranking-row:focus-visible {
    outline: 2px solid var(--ptt-purple-100, #5B2FD4);
    outline-offset: -2px;
  }

  .rank-number {
    font-weight: 700;
    color: var(--ptt-gray-100, #8c8c8c);
    width: 2.5rem;
  }

  .employee-name {
    font-weight: 600;
  }

  .total-value {
    font-weight: 700;
    color: var(--ptt-purple-100, #5B2FD4);
  }

  /* === Breakdown === */
  .breakdown-table .weekday {
    text-transform: capitalize;
    color: var(--ptt-gray-200, #565656);
  }

  .breakdown-table .punch-time {
    font-variant-numeric: tabular-nums;
    color: var(--ptt-gray-300, #323232);
  }

  .breakdown-row--issue td {
    background: var(--ptt-yellow-5, #fffbea);
  }

  .breakdown-row--inferred-falta td,
  .breakdown-row--aej-absence td {
    background: var(--ptt-red-10, #fdecea);
    color: var(--ptt-gray-300, #323232);
  }

  .breakdown-row--folga td {
    background: var(--ptt-gray-5, #f5f5f5);
    color: var(--ptt-gray-200, #555);
  }

  .empty-row-summary {
    display: inline-block;
    font-style: italic;
    color: var(--ptt-gray-200, #555);
    font-size: 0.8125rem;
  }

  .status-badge--folga {
    background: var(--ptt-gray-10, #ececec);
    color: var(--ptt-gray-200, #555);
    cursor: help;
    width: auto;
    height: auto;
    padding: 2px 6px;
    border-radius: var(--ptt-radius-sm);
    font-size: 0.6875rem;
    font-weight: 600;
  }

  /* Saldo positivo = hora extra (verde, sobrou) */
  .saldo--overtime {
    color: var(--ptt-green-100, #27ae60);
    font-weight: 600;
  }

  /* Saldo negativo = déficit (vermelho, faltou) */
  .saldo--deficit {
    color: var(--ptt-red-100, #c0392b);
    font-weight: 600;
  }

  .saldo--unknown {
    color: var(--ptt-gray-100, #999);
    font-style: italic;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    font-size: 0.75rem;
  }

  .status-badge--ok {
    background: var(--ptt-green-10, #e8f8f0);
    color: var(--ptt-green-100, #27ae60);
  }

  .status-badge--issue {
    background: var(--ptt-yellow-10, #fef9e7);
    color: var(--ptt-yellow-100, #f39c12);
    cursor: help;
  }

  .status-badge--incomplete {
    background: var(--ptt-orange-10, #fff7ed);
    color: var(--ptt-orange-100, #fb7224);
    cursor: help;
    width: auto;
    height: auto;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 600;
    font-size: 0.6875rem;
    border-radius: var(--ptt-radius-sm);
  }

  .status-badge--partial-data {
    background: var(--ptt-gray-10, #f5f5f5);
    color: var(--ptt-gray-200, #565656);
    cursor: help;
    width: auto;
    height: auto;
    padding: 2px 6px;
    border-radius: var(--ptt-radius-sm, 4px);
    font-weight: 600;
    font-size: 0.6875rem;
    border: 1px solid var(--ptt-gray-20, #e7e7e7);
  }

  @media (max-width: 768px) {
    .metric-detail-page {
      padding: var(--ptt-space-4) var(--ptt-space-3);
    }
  }
</style>
